import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Restore session on page load — HttpOnly cookie is sent automatically
  useEffect(() => {
    api.get('/auth/me')
      .then(({ data }) => {
        setUser(data.user);
        setSubscriptions(data.subscriptions || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Cross-tab subscription sync
  // ip_subs_ts signals that another tab purchased — re-fetch subscriptions.
  // visibilitychange catches the case where the storage event fired while tab was hidden.
  useEffect(() => {
    const resync = () => {
      api.get('/auth/me').then(({ data }) => {
        setUser(data.user);
        setSubscriptions(data.subscriptions || []);
      }).catch(() => {});
    };

    const onStorage = (e) => { if (e.key === 'ip_subs_ts') resync(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') resync(); };

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const register = useCallback(async (full_name, email, password, phone) => {
    const { data } = await api.post('/auth/register', { full_name, email, password, ...(phone ? { phone } : {}) });
    setUser(data.user);
    setSubscriptions([]);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  const loginWithGoogle = useCallback(async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (_) {}
    sessionStorage.removeItem('ip_impersonate_token');
    setUser(null);
    setSubscriptions([]);
  }, []);

  const hasActiveSubscription = useCallback(
    (roleId) => subscriptions.some((s) =>
      s.status === 'active' && (
        s.job_role_id === roleId ||
        s.tier === 'full_access' ||
        s.plan_type === 'full_access' ||
        s.plan_type === 'job_seeker'
      )
    ),
    [subscriptions]
  );

  const refreshSubscriptions = useCallback(async () => {
    if (!user) return;
    const { data } = await api.get('/auth/me');
    setSubscriptions(data.subscriptions || []);
    localStorage.setItem('ip_subs_ts', Date.now().toString());
  }, [user]);

  const value = {
    user,
    subscriptions,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    loginWithGoogle,
    logout,
    hasActiveSubscription,
    refreshSubscriptions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
